import { FC } from "react";
import { withPage } from "../../components/Page";
import styles from "./DemoGuide.module.scss";

const DemoGuide: FC = () => {
  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <span className={styles.eyebrow}>PULSE PLATFORM CONCEPT</span>
        <h1 className={styles.title}>Pulse Modernisation Demo</h1>
        <p className={styles.subtitle}>
          Explore how Pulse could be modernised and expanded while retaining its
          familiar structure, trusted information and core purpose.
        </p>
      </div>

      <div className={styles.demoSplitWrapper}>
        <div className={styles.benefitsCard}>
          <h3>Key Benefits</h3>
          <ul className={styles.benefitsList}>
            <li>
              <strong>Find information faster</strong> — less time searching
              through documents & drawings.
            </li>
            <li>
              <strong>Access information anywhere</strong> — desktop, tablet and
              mobile, including on site.
            </li>
            <li>
              <strong>Reduce manual effort</strong> — make everyday information
              easier to find & manage.
            </li>
            <li>
              <strong>Improve confidence</strong> — clearer, more accessible
              building documentation.
            </li>
            <li>
              <strong>Preserve what works</strong> — modernise without
              disrupting familiar structures.
            </li>
            <li>
              <strong>Build for the future</strong> — add capabilities
              incrementally as needs evolve.
            </li>
          </ul>
        </div>

        <div className={styles.introBox}>
          <div>
            <p>
              This is a concept demo showing how Pulse could evolve. It
              demonstrates a selection of potential improvements and new
              capabilities designed to make technical building information
              faster to find and easier to use in practice.
            </p>
            <p
              style={{
                color: "#dc2626",
                fontWeight: 700,
                fontSize: "1.35rem",
                margin: "1rem 0",
                lineHeight: 1.45,
              }}
            >
              ⚠️ Please note: Links to pages and underlying documents have
              purposely NOT been added throughout this prototype, except for the
              Westco Ductwork Design Criteria.pdf file. This is entirely a
              demonstration environment, so expect many fake links that won't
              work and are only demonstrative.
            </p>
            <p
              style={{
                color: "var(--color-text-primary)",
                fontWeight: 600,
                fontSize: "1.35rem",
                margin: "0.8rem 0 0.4rem",
              }}
            >
              When exploring this demo, notice and test these 8 key areas:
            </p>
            <ol
              style={{
                margin: "0 0 1rem",
                paddingLeft: "1.8rem",
                fontSize: "1.3rem",
                color: "var(--color-text-primary)",
                lineHeight: 1.55,
              }}
            >
              <li>
                <strong>Search engine</strong> (
                <a
                  href="/search"
                  style={{
                    color: "var(--color-blue)",
                    textDecoration: "underline",
                  }}
                >
                  /search
                </a>
                )
              </li>
              <li>
                <strong>Category page</strong> (
                <a
                  href="/design-criteria/mechanical"
                  style={{
                    color: "var(--color-blue)",
                    textDecoration: "underline",
                  }}
                >
                  /design-criteria/mechanical
                </a>
                )
              </li>
              <li>
                <strong>Drawings page</strong> (
                <a
                  href="/drawings"
                  style={{
                    color: "var(--color-blue)",
                    textDecoration: "underline",
                  }}
                >
                  /drawings
                </a>
                )
              </li>
              <li>
                <strong>QR code concept</strong> (
                <a
                  href="/asset-data/ahu-003"
                  style={{
                    color: "var(--color-blue)",
                    textDecoration: "underline",
                  }}
                >
                  /asset-data/ahu-003
                </a>
                )
              </li>
              <li>
                <strong>Ask AI concepts</strong> (Contextual buttons on pages &
                PDFs)
              </li>
              <li>
                <strong>Responsive design</strong> (Mobile, tablet & desktop)
              </li>
              <li>
                <strong>Modernised login</strong> (
                <a
                  href="/login"
                  style={{
                    color: "var(--color-blue)",
                    textDecoration: "underline",
                  }}
                >
                  /login
                </a>
                )
              </li>
              <li>
                <strong>Future capabilities</strong> (
                <a
                  href="/demo"
                  style={{
                    color: "var(--color-blue)",
                    textDecoration: "underline",
                  }}
                >
                  /demo
                </a>
                )
              </li>
            </ol>
          </div>
          <p className={styles.note}>
            Note: Some functionality and data in this environment are
            illustrative and not connected to live systems.
          </p>
        </div>
      </div>

      <section className={styles.section} aria-labelledby="what-to-try-heading">
        <h2 id="what-to-try-heading" className={styles.sectionTitle}>
          What to try in this demo
        </h2>
        <p className={styles.sectionDesc}>
          Core capabilities and modernised features available to test right now.
        </p>

        <div className={styles.tryGrid}>
          <div className={styles.tryCard}>
            <div>
              <h3>Improved Search</h3>
              <p>
                Fast, build-time indexed search combining page content and
                structured document metadata.
              </p>
              <div className={styles.exampleList}>
                <span className={styles.exampleTitle}>Try searching for:</span>
                <ul>
                  <li>
                    <code>MEIT design criteria</code>
                  </li>
                  <li>
                    <code>Westco Ductwork Design Criteria.pdf</code>
                  </li>
                  <li>
                    <code>AHU-003</code>
                  </li>
                </ul>
              </div>
            </div>
            <p className={styles.futureNote}>
              <strong>Future direction:</strong> Search could potentially be
              extended to index the full contents of PDFs and technical
              documents, making large document collections easier to
              interrogate.
            </p>
          </div>

          <div className={styles.tryCard}>
            <div>
              <h3>Ask AI</h3>
              <p>
                A contextual assistant concept providing quick answers about
                assets, equipment manuals, and technical spaces.
              </p>
              <div className={styles.exampleList}>
                <span className={styles.exampleTitle}>Key use cases:</span>
                <ul>
                  <li>Ask questions about individual assets or rooms</li>
                  <li>Find relevant maintenance or installation info</li>
                  <li>Answers linked back to underlying documents</li>
                </ul>
              </div>
            </div>
            <p className={styles.futureNote}>
              <strong>Future direction:</strong> Interrogating wider building
              documentation and summarising complex O&M records on demand.
            </p>
          </div>

          <div className={styles.tryCard}>
            <div>
              <h3>Mobile, Tablet & Desktop</h3>
              <p>
                Fully responsive layouts designed for engineers, facility
                managers and contractors working on site or in plant rooms.
              </p>
              <div className={styles.exampleList}>
                <span className={styles.exampleTitle}>Practical benefit:</span>
                <ul>
                  <li>Quick access from mobile devices in plant rooms</li>
                  <div>Streamlined navigation on tablets and laptops</div>
                </ul>
              </div>
            </div>
            <p className={styles.futureNote}>
              <strong>Current state:</strong> Fully implemented responsive
              design across all existing screens and viewports.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.qrSection}>
        <div className={styles.qrContent}>
          <span className={styles.eyebrow} style={{ color: "#38bdf8" }}>
            FIELD ACCESSIBILITY
          </span>
          <h3>QR / Pulse Codes Concept</h3>
          <p>
            A unique Pulse Code could be attached to physical assets, rooms or
            locations. Scanning the code with a standard phone camera would
            immediately identify the equipment and open the relevant Pulse
            information without navigating hierarchies.
          </p>
          <div className={styles.qrTags}>
            <span>Plant & Equipment</span>
            <span>Assets</span>
            <span>Rooms</span>
            <span>Plant Rooms</span>
            <span>Building Areas</span>
          </div>
          <p className={styles.qrFuture}>
            This could be introduced incrementally, starting with the most
            critical assets or plant rooms rather than requiring complete site
            tagging immediately.
          </p>
        </div>
      </section>

      <section
        className={styles.section}
        aria-labelledby="future-modules-heading"
      >
        <h2 id="future-modules-heading" className={styles.sectionTitle}>
          Other Potential Capabilities
        </h2>
        <p className={styles.sectionDesc}>
          Modular extensions that could be investigated based on organisational
          requirements.
        </p>

        <div className={styles.modulesGrid}>
          <div className={styles.moduleCard}>
            <h4>Personalised information</h4>
            <p>Recently viewed documents, bookmarks, and useful shortcuts.</p>
          </div>
          <div className={styles.moduleCard}>
            <h4>Custom dashboards</h4>
            <p>
              Tailored views for facilities teams, contractors, and building
              owners.
            </p>
          </div>
          <div className={styles.moduleCard}>
            <h4>AI document search</h4>
            <p>Semantic search across thousands of technical manuals.</p>
          </div>
          <div className={styles.moduleCard}>
            <h4>AI building assistant</h4>
            <p>
              Interrogate and summarise wider building handover information.
            </p>
          </div>
          <div className={styles.moduleCard}>
            <h4>Warranty & certificates</h4>
            <p>Expiry tracking, compliance records, and renewal queries.</p>
          </div>
          <div className={styles.moduleCard}>
            <h4>PPM schedules & Tasks</h4>
            <p>
              Planned preventative maintenance tracking and open action items.
            </p>
          </div>
          <div className={styles.moduleCard}>
            <h4>PDF document viewer</h4>
            <p>
              In-browser viewing with page navigation and annotation support.
            </p>
          </div>
          <div className={styles.moduleCard}>
            <h4>Advanced user management</h4>
            <p>
              Temporary contractor accounts, restricted permissions, and audit
              logs.
            </p>
          </div>
        </div>
        <p className={styles.modulesDisclaimer}>
          These are potential areas for investigation rather than a fixed
          development plan. Each would be assessed for feasibility, security,
          governance, practical value and fit with the organisation's
          requirements.
        </p>
      </section>

      <section
        className={styles.section}
        aria-labelledby="demo-features-heading"
      >
        <h2 id="demo-features-heading" className={styles.sectionTitle}>
          Other Demo Features
        </h2>
        <p className={styles.sectionDesc}>
          UI refinements and navigation enhancements explored in this prototype.
        </p>

        <div className={styles.otherGrid}>
          <div className={styles.tryCard}>
            <h3>Modernised Login</h3>
            <p>
              The demo includes a <code>/login</code> page demonstrating a
              potential modernised sign-in experience with secure branding.
            </p>
            <p className={styles.futureNote}>
              Note: This is an illustrative concept and is not connected to live
              authentication.
            </p>
          </div>
          <div className={styles.tryCard}>
            <h3>Improved UI & Navigation</h3>
            <p>
              Cleaner navigation, contextual breadcrumbs, optimized screen
              space, and responsive layouts designed to retain familiar Pulse
              structures.
            </p>
            <p className={styles.futureNote}>
              Designed to make daily operational tasks faster and more
              intuitive.
            </p>
          </div>
        </div>
      </section>

      <div className={styles.disclaimerBox}>
        <h4>Important demo notes</h4>
        <ul>
          <li>
            <strong>This is a concept demo, not a production system.</strong>
          </li>
          <li>Some pages and links are illustrative.</li>
          <li>Some data is fictional or demo data.</li>
          <li>
            The purpose is to demonstrate what could be possible through
            incremental modernisation.
          </li>
          <li>
            Any future functionality would be carefully assessed before
            implementation.
          </li>
        </ul>
      </div>

      <div className={styles.closingBox}>
        <h3>A Collaborative Starting Point</h3>
        <p>
          The ideas shown here are a starting point. Other workflows,
          frustrations or requirements identified by the organisation could also
          be explored and developed.
        </p>
      </div>
    </div>
  );
};

export default withPage(DemoGuide)({
  meta: {
    title: "Pulse Modernisation Demo Guide · Pulse",
    description:
      "Learn what this Pulse modernisation demo environment shows, what you can try, and future possibilities.",
  },
  breadcrumbs: [{ label: "Pulse", href: "/" }, { label: "Demo Guide" }],
});
